/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useState } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import {
    formatTimeSeriesNumericValue,
    resolveTimeSeriesTrafficLightLegendCondition,
    resolveTimeSeriesTrafficLightLabel,
    resolveTimeSeriesTrafficLightSeverity,
    type TimeSeriesTrafficLightConfig,
    type TimeSeriesTrafficLightSeverity,
} from '@/lib/timeSeries/timeSeriesValue';

interface TrafficLightLegendProps {
    fractionDigits?: number;
    title?: string;
    trafficLight: TimeSeriesTrafficLightConfig;
    unit?: string | null;
    value: number | null;
}

function getSeverityLabel(
    severity: TimeSeriesTrafficLightSeverity,
    trafficLight: TimeSeriesTrafficLightConfig,
    t: (key: string) => string,
): string {
    let defaultLabel: string;

    switch (severity) {
        case 'danger':
            defaultLabel = t('Error');
            break;
        case 'warning':
            defaultLabel = t('Warning');
            break;
        case 'implausible':
            defaultLabel = t('Implausible');
            break;
        case 'success':
        default:
            defaultLabel = t('OK');
    }

    return resolveTimeSeriesTrafficLightLabel(severity, trafficLight, defaultLabel);
}

function getOperatorSymbol(operator: 'lt' | 'lte' | 'gt' | 'gte'): string {
    return { gt: '>', gte: '>=', lt: '<', lte: '<=' }[operator];
}

function formatValue(
    value: number,
    locale: string,
    fractionDigits: number | undefined,
    unit: string | null | undefined,
): string {
    const formattedValue = formatTimeSeriesNumericValue(value, locale, fractionDigits);

    return unit ? `${formattedValue} ${unit}` : formattedValue;
}

function TrafficLightLegend({
    fractionDigits,
    title,
    trafficLight,
    unit,
    value,
}: TrafficLightLegendProps) {
    const { locale, t } = useI18n();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const severity = resolveTimeSeriesTrafficLightSeverity(value, trafficLight);
    const severities = Array.from(new Set([
        ...trafficLight.rules.map((rule) => rule.severity),
        ...(trafficLight.fallback ? [trafficLight.fallback] : []),
    ]));
    const titles = new Map(severities.map((itemSeverity) => {
        const condition = resolveTimeSeriesTrafficLightLegendCondition(itemSeverity, trafficLight);
        const label = getSeverityLabel(itemSeverity, trafficLight, t);

        return [
            itemSeverity,
            condition
                ? `${label}: ${t('Value')} ${getOperatorSymbol(condition.operator)} ${formatValue(condition.value, locale, fractionDigits, unit)}`
                : label,
        ] as const;
    }));

    if (severities.length === 0) {
        return null;
    }

    return (
        <div className="time-series-traffic-light-legend">
            <div
                className="time-series-kpi__legend"
                role="button"
                tabIndex={0}
                aria-label={t('Open traffic light legend')}
                title={t('Open traffic light legend')}
                onClick={() => setIsModalOpen(true)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setIsModalOpen(true);
                    }
                }}
            >
                {severities.map((itemSeverity) => (
                    <div
                        key={itemSeverity}
                        className={`time-series-kpi__legend-item${severity === itemSeverity ? ' time-series-kpi__legend-item--active' : ''}`}
                        title={titles.get(itemSeverity)}
                    >
                        <span className={`time-series-kpi__legend-swatch time-series-kpi__legend-swatch--${itemSeverity}`} aria-hidden="true" />
                    </div>
                ))}
            </div>

            {isModalOpen ? (
                <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="traffic-light-legend-modal-title">
                    <div className="modal-box max-w-2xl">
                        <div className="mb-4 flex items-start justify-between gap-4">
                            <div className="flex min-w-0 flex-1 flex-col gap-1 pl-4 text-left">
                                <h2 id="traffic-light-legend-modal-title">{t('Traffic light legend')}</h2>
                                {title ? <h3>{title}</h3> : null}
                            </div>
                            <button type="button" className="btn btn-ghost btn-sm btn-circle" aria-label={t('Close')} onClick={() => setIsModalOpen(false)}>✕</button>
                        </div>
                        <div className="overflow-x-auto">
                            {value !== null ? (
                                <p className="mb-4 px-4 text-left text-sm font-medium text-base-content/80">
                                    <span className="text-base-content/60">{t('Current value')}:</span>{' '}
                                    <span className="text-base-content">{formatValue(value, locale, fractionDigits, unit)}</span>
                                </p>
                            ) : null}
                            <table className="table table-zebra">
                                <thead><tr><th>{t('State')}</th><th>{t('Rule')}</th></tr></thead>
                                <tbody>
                                    {severities.map((itemSeverity) => (
                                        <tr key={itemSeverity} className={severity === itemSeverity ? 'font-semibold' : undefined}>
                                            <td>
                                                <div className="inline-flex items-center gap-3">
                                                    <span className={`time-series-kpi__legend-item${severity === itemSeverity ? ' time-series-kpi__legend-item--active' : ''}`}>
                                                        <span className={`time-series-kpi__legend-swatch time-series-kpi__legend-swatch--${itemSeverity}`} aria-hidden="true" />
                                                    </span>
                                                    <span>{getSeverityLabel(itemSeverity, trafficLight, t)}</span>
                                                </div>
                                            </td>
                                            <td>{titles.get(itemSeverity) ?? getSeverityLabel(itemSeverity, trafficLight, t)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="modal-action">
                            <button type="button" className="btn btn-primary" onClick={() => setIsModalOpen(false)}>{t('Close')}</button>
                        </div>
                    </div>
                    <div className="modal-backdrop bg-black/40" onClick={() => setIsModalOpen(false)} />
                </div>
            ) : null}
        </div>
    );
}

export default TrafficLightLegend;
